import { Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnInit, Output, Renderer2, SimpleChanges } from "@angular/core";
import { DateService } from "@app/services/date.service";
import { VideoStreamType } from "../../enums/VideoStreamType";
import { ICameraFootageTimelineDisplaySection } from "../../models/CameraFootageTimeline.interfaces";
import { CameraFootageTimelineService } from "../../services/camera-footage-timeline.service";
import "./request-video-activity-chart.component.scss";

@Component({
    selector: 'request-video-activity-chart',
    template: require('./request-video-activity-chart.component.html')
})
export class RequestVideoActivityChartComponent implements OnInit, OnChanges {

    // bindings

    @Input() footageSections: ICameraFootageTimelineDisplaySection[];
    @Input() selectedSecondOfDay: number;
    @Input() loading: boolean;
    @Input() @HostBinding("class.disabled") disabled: boolean;
    @Output() selectedSecondChange = new EventEmitter<number>();

    // ---

    selected: boolean;

    selectedSection: ICameraFootageTimelineDisplaySection;
    selectedPercentage: number;
    selectedTime: Date;

    hoveredSecondOfDay: number;
    hoveredSection: ICameraFootageTimelineDisplaySection;
    hoveredPercentage: number;
    hoveredTime: Date;

    futureMask: ICameraFootageTimelineDisplaySection;
    totalSecondsInRange: number = 86400;

    selectedLabelFlipped: boolean;
    hoveredLabelFlipped: boolean;

    VideoStreamType = VideoStreamType;

    constructor(
        private readonly renderer: Renderer2,
        private readonly elementRef: ElementRef,
        private readonly dateService: DateService,
        private readonly cameraFootageTimelineService: CameraFootageTimelineService
    ) {
    }

    ngOnInit(): void {
        this.subscribeToEvents();
        this.render();       
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["selectedSecondOfDay"] || changes["footageSections"] || changes["videoLength"]) {

            this.render();
        }      
    }

    onMouseMove(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }

        this.setHoveredTimeUsingMouseEvent(event);
    }

    onMouseEnter(event: MouseEvent): void {
        this.clearHover();
    }

    onMouseLeave(event: MouseEvent): void {
        this.clearHover();
    }

    onMouseClickChart(event: MouseEvent): void {
        if (this.disabled) {
            return;
        }

        const offset: number = event.offsetX;
        const maxOffset: number = event["target"]["offsetWidth"];

        const percentageClicked: number = (offset / maxOffset) * 100;
        const newSecondOfDayClicked: number = +((percentageClicked * 86400) / 100).toFixed(0);

        this.selectSecondOfDay(newSecondOfDayClicked);
    }

    // ---

    private selectChart(): void {
        this.selected = true;
    }

    private deselectChart(): void {
        this.selected = false;
    }

    private selectSecondOfDay(newSecond: number): void {
        this.selectedSecondOfDay = newSecond;
        this.selectedSecondChange?.emit(newSecond);

        this.render();
    }

    private render(): void {

        this.selectedSection = null;

        this.selectedPercentage = parseFloat(((this.selectedSecondOfDay / this.totalSecondsInRange) * 100).toFixed(2));

        this.calculateSelectedLabelOffset();

        if (!this.footageSections?.[0]) {
            return;
        }

        this.selectedTime = this.dateService.getStartOfDay(new Date(this.footageSections[0].dateStart));
        this.selectedTime.setSeconds(this.selectedSecondOfDay);
        this.selectedSection = this.getFootageSectionForDateTime(this.selectedTime);

        this.setupFutureMask();
    }

    private setupFutureMask(): void {
        this.futureMask = null;

        if (!this.dateService.isSameDate(this.selectedTime, new Date())) {
            return;
        }

        const endOfToday: Date = this.dateService.getEndOfDay(new Date());

        this.futureMask = {
            footage: [],
            availableChannelIds: [],
            availableVideoStreamTypes: [],
            dateStart: new Date(),
            dateEnd: endOfToday,
            startPercentage: 0,
            widthPercentage: 0,
            label: null,
            hasMainStream: false,
            hasSubStream: false
        }

        this.cameraFootageTimelineService.setFootageSectionPercentages([this.futureMask]);
    }

    private setHoveredTimeUsingMouseEvent(event: MouseEvent): void {

        if (!this.footageSections?.length) {
            return;
        }

        const offset: number = event.offsetX;
        const maxOffset: number = event["target"]["offsetWidth"];

        this.hoveredPercentage = (offset / maxOffset) * 100;
        this.hoveredSecondOfDay = +((this.hoveredPercentage * 86400) / 100).toFixed(0);

        this.hoveredTime = this.dateService.getStartOfDay(new Date(this.footageSections[0].dateStart));
        this.hoveredTime.setSeconds(this.hoveredSecondOfDay);
        this.hoveredSection = this.getFootageSectionForDateTime(this.hoveredTime);

        this.calculateHoveredLabelOffset();
    }

    private clearHover(): void {
        this.hoveredSecondOfDay = null;
        this.hoveredSection = null;
        this.hoveredPercentage = null;
        this.hoveredTime = null;
    }

    private getFootageSectionForDateTime(date: Date): ICameraFootageTimelineDisplaySection {
        const sections: ICameraFootageTimelineDisplaySection[] = [];

        for (const footageSection of this.footageSections) {
            const matched = this.dateService.dateIsBetweenDates(new Date(date), footageSection.dateStart, footageSection.dateEnd, true, true);

            if (matched) {
                sections.push(footageSection);
            }
        }

        if (!sections?.length) {
            return null;
        }

        return sections.find(section => section.requested) || sections[0];
    }

    private subscribeToEvents(): void {
        this.renderer.listen(this.elementRef.nativeElement, 'mouseup', this.handleMouseUp.bind(this));
        this.renderer.listen(this.elementRef.nativeElement, 'keydown', this.handleKeyDown.bind(this));
    }

    private handleMouseUp(event: any): void {

        if (event.target?.offsetParent?.className?.indexOf("chart-container") > -1) {
            this.selectChart();
            return;
        }

        this.deselectChart();
    }

    private handleKeyDown(event: any): void {
        this.handleArrowsKeys(event);
    }

    private handleArrowsKeys(event: any): void {

        if (!this.selected) {
            return;
        }

        if (!event?.originalEvent?.code) {
            return;
        }

        if (event.originalEvent.code === "ArrowLeft") {
            this.selectSecondOfDay(this.selectedSecondOfDay - 60);
        }

        if (event.originalEvent.code === "ArrowRight") {
            this.selectSecondOfDay(this.selectedSecondOfDay + 60);
        }
    }

    private calculateSelectedLabelOffset(): void {
        this.selectedLabelFlipped = this.selectedSecondOfDay > (this.totalSecondsInRange - 60 * 60 * 2);
    }

    private calculateHoveredLabelOffset(): void {
        this.hoveredLabelFlipped = this.hoveredSecondOfDay > this.totalSecondsInRange - 60 * 60 * 2;
    }
}
